import React from 'react';
import { ShiftGuardianSummaryResponse, inboundInboxService } from '../../services/inboundInboxService';
import { GDashCheckType, GDashPopup } from './GDashPopup';

type Props = {
  userId: string;
  isAuthenticated: boolean;
  onOpenInbox: (item?: ShiftGuardianSummaryResponse['items'][number]) => void | Promise<boolean | void>;
};

const envEnabled = String(import.meta.env.VITE_INBOX_SHIFT_GUARDIAN || '').trim().toLowerCase() === 'true';
const TRIGGER_WINDOW_MINUTES = 5;
const STATUS_EVENT = 'gdash:scheduler-status';
const TRIGGER_EVENT = 'gdash:trigger-now';
const CLEAR_DISMISSALS_EVENT = 'gdash:clear-dismissals';
const MOUNT_EVENT = 'gdash:scheduler-mounted';

const getDatePartsInZone = (timeZone: string) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date());
  const map = new Map(parts.map(p => [p.type, p.value]));
  const date = `${map.get('year')}-${map.get('month')}-${map.get('day')}`;
  const hour = Number(map.get('hour') || 0);
  const minute = Number(map.get('minute') || 0);
  return { date, minuteOfDay: hour * 60 + minute };
};

const parseHHMM = (value: string, fallback: number) => {
  const m = /^(\d{2}):(\d{2})$/.exec(String(value || '').trim());
  if (!m) return fallback;
  const h = Number(m[1]);
  const mm = Number(m[2]);
  if (h < 0 || h > 23 || mm < 0 || mm > 59) return fallback;
  return h * 60 + mm;
};

const minuteToHHMM = (minute: number): string => {
  const normalized = ((minute % 1440) + 1440) % 1440;
  const h = Math.floor(normalized / 60);
  const m = normalized % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const getNowSafe = (timeZone: string): { date: string; minuteOfDay: number; hhmm: string; fallback: boolean } => {
  try {
    const now = getDatePartsInZone(timeZone);
    return { ...now, hhmm: minuteToHHMM(now.minuteOfDay), fallback: false };
  } catch (error) {
    console.warn('[G.DASH Scheduler] timezone formatter failed, using local time fallback', error);
    const d = new Date();
    const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const minuteOfDay = d.getHours() * 60 + d.getMinutes();
    return { date, minuteOfDay, hhmm: minuteToHHMM(minuteOfDay), fallback: true };
  }
};

type SchedulerStatus = {
  userId: string;
  timezone: string;
  currentDate: string;
  currentTime: string;
  nextCheckLabel: string;
  lastCheckAt: string;
  lastDecision: string;
  reason: string;
};

export const GDashScheduler: React.FC<Props> = ({ userId, isAuthenticated, onOpenInbox }) => {
  const [isPrimaryInstance, setIsPrimaryInstance] = React.useState(true);
  const [open, setOpen] = React.useState(false);
  const [checkType, setCheckType] = React.useState<GDashCheckType>('morning');
  const [summary, setSummary] = React.useState<ShiftGuardianSummaryResponse | null>(null);
  const remindTimeoutRef = React.useRef<number | null>(null);
  const instanceIdRef = React.useRef(`gdash-${Math.random().toString(36).slice(2)}`);
  const scheduledMinuteByTypeRef = React.useRef<Record<GDashCheckType, number>>({
    morning: 8 * 60,
    midday: 13 * 60,
    eod: 17 * 60 + 30,
    finish: 18 * 60,
  });

  const dismissKey = React.useCallback((date: string, type: GDashCheckType, minute?: number) => {
    const at = minuteToHHMM(typeof minute === 'number' ? minute : (scheduledMinuteByTypeRef.current[type] ?? 0));
    return `gdash.dismissed.${userId}.${date}.${type}.${at}`;
  }, [userId]);

  const emitStatus = React.useCallback((status: SchedulerStatus) => {
    window.dispatchEvent(new CustomEvent(STATUS_EVENT, { detail: status }));
  }, []);

  const shouldDebugLog = React.useCallback((role?: string) => {
    return import.meta.env.DEV || role === 'ADMIN' || role === 'SUPER_ADMIN';
  }, []);

  const emitMounted = React.useCallback((mounted: boolean, at?: string) => {
    const payload = { mounted, at: at || new Date().toISOString(), instanceId: instanceIdRef.current };
    window.dispatchEvent(new CustomEvent(MOUNT_EVENT, { detail: payload }));
  }, []);

  const triggerCheckType = React.useCallback(async (type: GDashCheckType) => {
    const detailed = await inboundInboxService.getShiftGuardianSummary({ checkType: type });
    if (!detailed.enabled) return detailed;
    setSummary(detailed);
    setCheckType(type);
    setOpen(true);
    return detailed;
  }, []);

  const runCheck = React.useCallback(async () => {
    const tzDefault = summary?.settings?.timezone || 'Africa/Dar_es_Salaam';
    const nowDefault = getNowSafe(tzDefault);
    const lastCheckAt = new Date().toISOString();
    console.debug('[G.DASH Scheduler] tick');
    console.debug(`[G.DASH Scheduler] currentTime=${nowDefault.hhmm}`);
    try {
      if (!isAuthenticated || !userId) {
        emitStatus({
          userId: userId || 'anonymous',
          timezone: tzDefault,
          currentDate: nowDefault.date,
          currentTime: nowDefault.hhmm,
          nextCheckLabel: 'n/a',
          lastCheckAt,
          lastDecision: 'not triggered',
          reason: 'not_authenticated',
        });
        return;
      }
      if (!envEnabled) {
        emitStatus({
          userId,
          timezone: tzDefault,
          currentDate: nowDefault.date,
          currentTime: nowDefault.hhmm,
          nextCheckLabel: 'n/a',
          lastCheckAt,
          lastDecision: 'not triggered',
          reason: 'disabled_by_frontend_env',
        });
        return;
      }
      if (open) {
        emitStatus({
          userId,
          timezone: tzDefault,
          currentDate: nowDefault.date,
          currentTime: nowDefault.hhmm,
          nextCheckLabel: 'popup_open',
          lastCheckAt,
          lastDecision: 'not triggered',
          reason: 'popup_already_open',
        });
        return;
      }

      const base = await inboundInboxService.getShiftGuardianSummary({ checkType: 'preview' });
      const role = base.debug?.userRole || '';
      const tz = base.settings?.timezone || 'Africa/Dar_es_Salaam';
      const now = getNowSafe(tz);
      if (!base.enabled || !base.settings) {
        emitStatus({
          userId,
          timezone: tz,
          currentDate: now.date,
          currentTime: now.hhmm,
          nextCheckLabel: 'n/a',
          lastCheckAt,
          lastDecision: 'not triggered',
          reason: base.reason || 'disabled',
        });
        if (shouldDebugLog(role)) {
          console.debug(`[G.DASH Scheduler] now=${now.hhmm} timezone=${tz}`);
          console.debug(`[G.DASH Scheduler] not triggered reason=${base.reason || 'disabled'}`);
        }
        return;
      }

      const checks: Array<{ key: GDashCheckType; enabled: boolean; minute: number }> = [
      { key: 'morning', enabled: base.settings.morningBriefEnabled !== false, minute: parseHHMM(base.settings.morningBriefTime, 8 * 60) },
      { key: 'midday', enabled: base.settings.middayCheckEnabled !== false, minute: parseHHMM(base.settings.middayCheckTime, 13 * 60) },
      { key: 'eod', enabled: base.settings.endOfDayCheckEnabled !== false, minute: parseHHMM(base.settings.endOfDayCheckTime, 17 * 60 + 30) },
      { key: 'finish', enabled: true, minute: parseHHMM(base.settings.shiftEndTime, 18 * 60) },
      ];
      scheduledMinuteByTypeRef.current = {
        morning: checks.find(c => c.key === 'morning')?.minute ?? 8 * 60,
        midday: checks.find(c => c.key === 'midday')?.minute ?? 13 * 60,
        eod: checks.find(c => c.key === 'eod')?.minute ?? (17 * 60 + 30),
        finish: checks.find(c => c.key === 'finish')?.minute ?? 18 * 60,
      };
      const enabledChecks = checks.filter(c => c.enabled);
      const nextCheck = enabledChecks
        .map(c => {
          const diff = c.minute >= now.minuteOfDay ? c.minute - now.minuteOfDay : (1440 - now.minuteOfDay + c.minute);
          return { ...c, diff };
        })
        .sort((a, b) => a.diff - b.diff)[0];
      let lastDecision = 'not triggered';
      let reason = 'outside trigger window';
      for (const c of checks) {
        if (!c.enabled) continue;
        const delta = now.minuteOfDay - c.minute;
        const dismissed = localStorage.getItem(dismissKey(now.date, c.key, c.minute)) === '1';
        const inWindow = delta >= 0 && delta <= TRIGGER_WINDOW_MINUTES;
        if (shouldDebugLog(role)) {
          console.debug(`[G.DASH Scheduler] now=${now.hhmm} timezone=${tz}`);
          console.debug(`[G.DASH Scheduler] checking ${c.key} scheduled=${minuteToHHMM(c.minute)} window=${TRIGGER_WINDOW_MINUTES}min dismissed=${dismissed} allowed=${inWindow}`);
        }
        if (!inWindow) continue;
        if (dismissed) {
          reason = `dismissed_today:${c.key}@${minuteToHHMM(c.minute)}`;
          continue;
        }
        const detailed = await triggerCheckType(c.key);
        if (!detailed?.enabled) {
          reason = detailed?.reason || `backend_disabled:${c.key}`;
          continue;
        }
        lastDecision = `triggered:${c.key}`;
        reason = 'triggered';
        if (shouldDebugLog(role)) {
          console.debug(`[G.DASH Scheduler] triggering ${c.key}`);
          console.debug(`[G.DASH Scheduler] popup opening type=${c.key}`);
        }
        break;
      }
      emitStatus({
        userId,
        timezone: tz,
        currentDate: now.date,
        currentTime: now.hhmm,
        nextCheckLabel: nextCheck ? `${nextCheck.key.toUpperCase()} at ${minuteToHHMM(nextCheck.minute)}` : 'n/a',
        lastCheckAt,
        lastDecision,
        reason,
      });
      if (shouldDebugLog(role) && lastDecision !== 'triggered') {
        console.debug(`[G.DASH Scheduler] not triggered reason=${reason}`);
      }
    } catch (error: any) {
      console.error('[G.DASH Scheduler] error', error);
      emitStatus({
        userId: userId || 'anonymous',
        timezone: tzDefault,
        currentDate: nowDefault.date,
        currentTime: nowDefault.hhmm,
        nextCheckLabel: 'n/a',
        lastCheckAt,
        lastDecision: 'scheduler error',
        reason: String(error?.message || 'Unknown scheduler error'),
      });
    }
  }, [dismissKey, emitStatus, isAuthenticated, open, shouldDebugLog, summary?.settings?.timezone, triggerCheckType, userId]);

  React.useEffect(() => {
    const guard = (window as any).__gdashSchedulerPrimaryInstanceId;
    if (guard && guard !== instanceIdRef.current) {
      setIsPrimaryInstance(false);
      return;
    }
    (window as any).__gdashSchedulerPrimaryInstanceId = instanceIdRef.current;
    setIsPrimaryInstance(true);
    const mountedAt = new Date().toISOString();
    document.body.setAttribute('data-gdash-mounted', 'true');
    emitMounted(true, mountedAt);
    console.log('[G.DASH Scheduler] mounted in App.tsx > GDashScheduler');
    const beat = window.setInterval(() => {
      emitMounted(true, new Date().toISOString());
    }, 30000);
    return () => {
      window.clearInterval(beat);
      if ((window as any).__gdashSchedulerPrimaryInstanceId === instanceIdRef.current) {
        delete (window as any).__gdashSchedulerPrimaryInstanceId;
      }
      document.body.removeAttribute('data-gdash-mounted');
      emitMounted(false, new Date().toISOString());
    };
  }, [emitMounted]);

  React.useEffect(() => {
    if (!isAuthenticated) return;
    if (!isPrimaryInstance) return;
    console.debug('[G.DASH Scheduler] starting interval');
    const runSchedulerTick = () => { void runCheck(); };
    runSchedulerTick();

    // Align to exact minute boundaries so HH:mm checks fire at the intended minute.
    let intervalId: number | null = null;
    const startAlignedInterval = () => {
      if (intervalId) window.clearInterval(intervalId);
      intervalId = window.setInterval(runSchedulerTick, 60000);
    };
    const msToNextMinute = Math.max(100, 60000 - (Date.now() % 60000) + 25);
    const alignTimeoutId = window.setTimeout(() => {
      runSchedulerTick();
      startAlignedInterval();
    }, msToNextMinute);

    // If the tab was background-throttled/slept, re-check immediately on resume.
    const onVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        console.debug('[G.DASH Scheduler] resumed, running immediate tick');
        runSchedulerTick();
      }
    };
    window.addEventListener('focus', onVisibilityOrFocus);
    document.addEventListener('visibilitychange', onVisibilityOrFocus);

    return () => {
      window.clearTimeout(alignTimeoutId);
      if (intervalId) window.clearInterval(intervalId);
      window.removeEventListener('focus', onVisibilityOrFocus);
      document.removeEventListener('visibilitychange', onVisibilityOrFocus);
    };
  }, [isAuthenticated, isPrimaryInstance, runCheck]);

  React.useEffect(() => {
    if (!isAuthenticated || !userId || !isPrimaryInstance) return;
    const onTriggerNow = async (event: Event) => {
      const detail = (event as CustomEvent<{ checkType?: GDashCheckType }>).detail || {};
      const type = detail.checkType;
      if (!type) return;
      try {
        const tz = summary?.settings?.timezone || 'Africa/Dar_es_Salaam';
        const now = getNowSafe(tz);
        const role = summary?.debug?.userRole || '';
        console.debug(`[G.DASH Scheduler] trigger-now received type=${type}`);
        const res = await triggerCheckType(type);
        emitStatus({
          userId,
          timezone: tz,
          currentDate: now.date,
          currentTime: now.hhmm,
          nextCheckLabel: 'manual trigger',
          lastCheckAt: new Date().toISOString(),
          lastDecision: res?.enabled ? `triggered:${type}` : 'not triggered',
          reason: res?.enabled ? 'triggered' : (res?.reason || 'disabled'),
        });
        if (shouldDebugLog(role)) {
          if (res?.enabled) {
            console.debug(`[G.DASH Scheduler] triggering ${type}`);
            console.debug(`[G.DASH Scheduler] popup opening type=${type}`);
          } else {
            console.debug(`[G.DASH Scheduler] not triggered reason=${res?.reason || 'disabled'}`);
          }
        }
      } catch (error: any) {
        console.error('[G.DASH Scheduler] error', error);
        const tz = summary?.settings?.timezone || 'Africa/Dar_es_Salaam';
        const now = getNowSafe(tz);
        emitStatus({
          userId,
          timezone: tz,
          currentDate: now.date,
          currentTime: now.hhmm,
          nextCheckLabel: 'manual trigger',
          lastCheckAt: new Date().toISOString(),
          lastDecision: 'scheduler error',
          reason: String(error?.message || 'Unknown scheduler error'),
        });
      }
    };
    const onClearDismissals = () => {
      const tz = summary?.settings?.timezone || 'Africa/Dar_es_Salaam';
      const now = getNowSafe(tz);
      const prefix = `gdash.dismissed.${userId}.${now.date}.`;
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith(prefix)) localStorage.removeItem(key);
      });
      emitStatus({
        userId,
        timezone: tz,
        currentDate: now.date,
        currentTime: now.hhmm,
        nextCheckLabel: 'dismissals cleared',
        lastCheckAt: new Date().toISOString(),
        lastDecision: 'not triggered',
        reason: 'dismissals_cleared',
      });
    };
    window.addEventListener(TRIGGER_EVENT, onTriggerNow as EventListener);
    window.addEventListener(CLEAR_DISMISSALS_EVENT, onClearDismissals as EventListener);
    return () => {
      window.removeEventListener(TRIGGER_EVENT, onTriggerNow as EventListener);
      window.removeEventListener(CLEAR_DISMISSALS_EVENT, onClearDismissals as EventListener);
    };
  }, [emitStatus, isAuthenticated, isPrimaryInstance, shouldDebugLog, summary?.debug?.userRole, summary?.settings?.timezone, triggerCheckType, userId]);

  React.useEffect(() => () => {
    if (remindTimeoutRef.current) window.clearTimeout(remindTimeoutRef.current);
  }, []);

  if (!isAuthenticated || !userId || !isPrimaryInstance) return null;

  const closeWithDismiss = () => {
    const tz = summary?.settings?.timezone || 'Africa/Dar_es_Salaam';
    const now = getNowSafe(tz);
    localStorage.setItem(dismissKey(now.date, checkType, scheduledMinuteByTypeRef.current[checkType]), '1');
    setOpen(false);
  };

  const remindLater = () => {
    setOpen(false);
    if (remindTimeoutRef.current) window.clearTimeout(remindTimeoutRef.current);
    remindTimeoutRef.current = window.setTimeout(async () => {
      const detailed = await inboundInboxService.getShiftGuardianSummary({ checkType });
      if (detailed.enabled) {
        setSummary(detailed);
        setOpen(true);
      }
    }, 15 * 60 * 1000);
  };

  return (
    <GDashPopup
      open={open}
      checkType={checkType}
      summary={summary}
      onRefreshSummary={setSummary}
      onClose={closeWithDismiss}
      onOpenInbox={onOpenInbox}
      onRemindLater={remindLater}
      preview={false}
    />
  );
};
